import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ThemeToggle from './ThemeToggle';
import './Navigation.css';

const Navigation = ({ isOpen, onToggle }) => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { path: '/', label: 'HOME', icon: 'H' },
    { path: '/tracks', label: 'TRACKS', icon: 'T' },
    { path: '/about', label: 'ABOUT', icon: 'A' }
  ];

  return (
    <>
      {/* Main Navigation Bar */}
      <motion.nav 
        className={`navigation ${scrolled ? 'navigation--scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
      >
        <div className="navigation__container">
          {/* Logo */}
          <Link to="/" className="navigation__logo">
            <div className="navigation__logo-icon">
              <div className="navigation__logo-inner">C</div>
            </div>
            <span className="navigation__logo-text">CYPHER</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="navigation__menu">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`navigation__link ${
                  location.pathname === item.path ? 'navigation__link--active' : ''
                }`}
              >
                <span className="navigation__link-icon">{item.icon}</span>
                <span className="navigation__link-text">{item.label}</span>
              </Link>
            ))}
            
            {/* Theme Toggle */}
            <ThemeToggle showLabel={false} size="small" />
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className={`navigation__toggle ${isOpen ? 'navigation__toggle--open' : ''}`}
            onClick={onToggle}
            aria-label="Toggle navigation menu"
          >
            <span className="navigation__toggle-line"></span>
            <span className="navigation__toggle-line"></span>
            <span className="navigation__toggle-line"></span>
          </button>

          {/* System Status Indicator */}
          <div className="navigation__status">
            <div className="navigation__status-dot"></div>
            <span className="navigation__status-text">ONLINE</span>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Navigation Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="navigation__mobile"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="navigation__mobile-header">
              <div className="navigation__mobile-logo">
                <div className="navigation__mobile-logo-icon">C</div>
                <span>CYPHER.NET</span>
              </div>
              <button 
                className="navigation__mobile-close"
                onClick={onToggle}
              >
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            <div className="navigation__mobile-menu">
              {navigationItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <Link
                    to={item.path}
                    className={`navigation__mobile-link ${
                      location.pathname === item.path ? 'navigation__mobile-link--active' : ''
                    }`}
                    onClick={onToggle}
                  >
                    <div className="navigation__mobile-link-icon">
                      {item.icon}
                    </div>
                    <span className="navigation__mobile-link-text">
                      {item.label}
                    </span>
                    <div className="navigation__mobile-link-arrow">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                      </svg>
                    </div>
                  </Link>
                </motion.div>
              ))}
              
              {/* Mobile Theme Toggle */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * navigationItems.length }}
                className="navigation__mobile-theme-toggle"
              >
                <ThemeToggle showLabel={true} size="medium" />
              </motion.div>
            </div>

            <div className="navigation__mobile-footer">
              <div className="navigation__mobile-status">
                <div className="navigation__mobile-status-grid">
                  <div className="navigation__mobile-status-item">
                    <span className="navigation__mobile-status-label">STATUS</span>
                    <span className="navigation__mobile-status-value">ONLINE</span>
                  </div>
                  <div className="navigation__mobile-status-item">
                    <span className="navigation__mobile-status-label">NETWORK</span>
                    <span className="navigation__mobile-status-value">CYPHER.NET</span>
                  </div>
                  <div className="navigation__mobile-status-item">
                    <span className="navigation__mobile-status-label">TIME</span>
                    <span className="navigation__mobile-status-value">
                      {new Date().toLocaleTimeString('en-US', { hour12: false })}
                    </span>
                  </div>
                  <div className="navigation__mobile-status-item">
                    <span className="navigation__mobile-status-label">MODE</span>
                    <span className="navigation__mobile-status-value">TACTICAL</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;